import { oneLine } from 'common-tags';

import { RichInline } from '@/atoms';
import { SliderCard } from '@/molecules';
import { Postal } from '@/molecules/postal';

import { blockSpacing, eyebrowClass, textStyleClass } from '@/utils';
import { getRealUrl } from '@/utils/utils';

/**
 * Sección "Postales" del Figma HoM: postales con rotaciones alternas +
 * offset vertical para el efecto "fotos sobre la mesa".
 */
export const ComponentRevisadasPostales = ({
  Eyebrow,
  Titulo,
  Descripcion,
  EyebrowEstilo,
  TituloEstilo,
  DescripcionEstilo,
  postales,
  FondoSeccion,
  Espaciado,
  // Toggle Strapi opt-in: en móvil pasa el row de postales a un
  // SliderCard horizontal (swipe). Default null/false → comportamiento
  // histórico (stack vertical en móvil, fila rotada en desktop).
  MobileSlider,
}: any) => {
  if (!postales?.length) return null;
  const eyebrowCls = eyebrowClass(EyebrowEstilo);
  const tituloCls = textStyleClass(TituloEstilo);
  const descripcionCls = textStyleClass(DescripcionEstilo);

  return (
    <div
      className="w-full"
      style={FondoSeccion ? { backgroundColor: FondoSeccion } : undefined}
    >
      {/* `--container-px-mobile` da margen lateral en móvil cuando el tenant
          lo configura en Strapi (`ThemeConfiguration.containerPaddingX.PxMobile`).
          Default 0 → tenants antiguos siguen full-bleed como hasta ahora. */}
      <section
        className={`${blockSpacing(Espaciado)} mx-auto w-full max-w-(--wrapper-size) px-[var(--container-px-mobile,0px)] lg:px-0`}
      >
        <div className="flex max-w-full flex-col gap-2">
          <div className="flex flex-col items-end self-start">
            {Eyebrow && (
              <span
                className={oneLine`font-accent text-primary-500 ${
                  eyebrowCls ?? 'text-3xl leading-10 font-normal'
                }`}
              >
                {Eyebrow}
              </span>
            )}

            {Titulo && (
              <RichInline
                as="h2"
                html={Titulo}
                className={oneLine`text-headline text-text-primary font-primary ${
                  tituloCls ?? 'text-headline-h1 font-bold'
                }`}
              />
            )}
          </div>

          {Descripcion && (
            <p
              className={oneLine`text-body text-text-primary ${
                descripcionCls ?? 'text-body-l'
              }`}
            >
              {Descripcion}
            </p>
          )}
        </div>

        {(() => {
          // Las clases `lg:` (rotaciones + offsets) sólo aplican en desktop;
          // en el SliderCard mobile son neutras. Renderizamos el mismo Postal
          // con dos wrappers distintos: `<div>` para el slider (cada slide ya
          // es un `<li>` dentro de Splide, evitamos nesting inválido) y `<li>`
          // para el `<ul>` desktop.
          const itemClassName = (index: number) => {
            const isEven = index % 2 === 0;
            return [
              'transition-transform',
              isEven ? 'lg:-rotate-4' : 'lg:rotate-4',
              index === 0 ? '' : 'lg:-ml-[138px]',
              isEven ? '' : 'lg:translate-y-[163px]',
            ]
              .filter(Boolean)
              .join(' ');
          };

          const renderPostal = (
            postal: any,
            index: number,
            extraClass = '',
          ) => (
            <Postal
              className={extraClass}
              name={postal.Nombre}
              description={postal.Descripcion}
              image={{
                src: getRealUrl(postal.Imagen?.url),
                alt: postal.Imagen?.alternativeText || postal.Nombre,
              }}
              href={postal.Link}
              ctaPosition={index % 2 === 0 ? 'top' : 'bottom'}
              nameFontFamily={postal.NombreFontFamily}
              nameStyleClass={textStyleClass(postal.NombreEstilo)}
            />
          );

          if (MobileSlider) {
            // Mobile: SliderCard horizontal con swipe — la activa va recta y
            // centrada, las vecinas ladeadas (replica del look "fotos sobre
            // la mesa" del desktop pero a escala mobile).
            // Desktop (lg+): el grid rotado histórico (no afecta al slider).
            return (
              <>
                <div className="mt-8 lg:hidden">
                  <SliderCard
                    perPage={1.4}
                    gap={56}
                    focus="center"
                    padding={50}
                    updateOnMove
                  >
                    {postales.map((postal: any, index: number) => {
                      const isEven = index % 2 === 0;
                      // Rotación base alternada (par → izquierda, impar →
                      // derecha). Se anula via Tailwind variant cuando la
                      // slide queda como `.splide__slide.is-active`.
                      const tilt = isEven ? '-rotate-3' : 'rotate-3';
                      return (
                        <div
                          key={postal.documentId ?? index}
                          // `data-postal-card` permite que un CSS rule en
                          // `globals.css` resetee la rotación cuando la
                          // slide queda como `.splide__slide.is-active`
                          // (variant arbitrario de Tailwind v4 con `_&`
                          // anidado no se estaba generando aquí).
                          data-postal-card=""
                          // La postal es más ancha que su slide (por eso las
                          // vecinas se solapan y dan el look "apilado"); sin
                          // centrar, desbordaba sólo a la derecha y la activa
                          // salía descentrada. `flex justify-center` la centra
                          // sobre su slide (que Splide ya centra con `focus`).
                          className={`flex justify-center transition-transform duration-300 ${tilt}`}
                          style={{ zIndex: index + 1 }}
                        >
                          {/* `shrink-0`: sin esto el flex encoge la postal al
                              ancho del slide y pierde el desbordamiento que da
                              el look apilado. */}
                          {renderPostal(postal, index, 'shrink-0')}
                        </div>
                      );
                    })}
                  </SliderCard>
                </div>
                <ul className="hidden lg:mt-20 lg:flex lg:min-h-[483px] lg:flex-row lg:flex-wrap lg:items-start lg:justify-center lg:gap-0">
                  {postales.map((postal: any, index: number) => (
                    <li
                      key={postal.documentId ?? index}
                      className={itemClassName(index)}
                      style={{ zIndex: index + 1 }}
                    >
                      {renderPostal(postal, index)}
                    </li>
                  ))}
                </ul>
              </>
            );
          }

          return (
            <ul className="mt-12 flex flex-col items-center gap-8 lg:mt-20 lg:min-h-[483px] lg:flex-row lg:flex-wrap lg:items-start lg:justify-center lg:gap-0">
              {postales.map((postal: any, index: number) => (
                <li
                  key={postal.documentId ?? index}
                  className={itemClassName(index)}
                  style={{ zIndex: index + 1 }}
                >
                  {renderPostal(postal, index)}
                </li>
              ))}
            </ul>
          );
        })()}
      </section>
    </div>
  );
};

ComponentRevisadasPostales.displayName = 'ComponentRevisadasPostales';
